import React, { useEffect, useState, useCallback } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;
const ACCENT = '#818cf8';
const CATEGORIES = ['Product/Type-approval','EMC','Environmental','Safety','Ingress/Enclosure',
  'Interfaces','Workmanship','Process','ESD','Quality Management','Environmental Management',
  'Material/Regulatory','Calibration/Measurement','Other'];

function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}
const BLANK = { code:'', title:'', authority:'', category:'Other', scope:'', edition:'', url:'',
  appliesToProduction:false, supplierApplicable:false, active:true, notes:'' };

export default function StandardsLibrary() {
  const { getToken } = useAuth(); const { user } = useUser();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const canManage = ['admin','quality-manager','production-engineer','service'].includes(role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');
  const [supplierOnly, setSupplierOnly] = useState(false);
  const [editItem, setEditItem] = useState(null);   // object being added/edited
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (supplierOnly) params.set('supplierApplicable', 'true');
      if (canManage) params.set('includeInactive', 'true');
      const res = await window.fetch(`${API_BASE}/quality/standards?${params}`, { headers:{ Authorization:`Bearer ${token}` } });
      const d = await res.json();
      if (d.ok) setItems(d.standards || []);
    } catch {} finally { setLoading(false); }
  }, [getToken, category, supplierOnly, canManage]);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    if (!window.confirm('Seed / refresh the standards catalogue from the curated NI list?')) return;
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/standards/seed`, { method:'POST', headers:{ Authorization:`Bearer ${token}` } });
      const d = await res.json();
      if (!d.ok) throw new Error(d.error||'Seed failed');
      load();
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const save = async () => {
    if (!editItem.code.trim()) { setError('Code is required'); return; }
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const isNew = !editItem._id;
      const res = await window.fetch(`${API_BASE}/quality/standards${isNew?'':`/${editItem._id}`}`, {
        method: isNew?'POST':'PUT', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify(editItem),
      });
      const d = await res.json();
      if (!d.ok) throw new Error(d.error||'Save failed');
      setEditItem(null); load();
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const del = async (it) => {
    if (!window.confirm(`Delete ${it.code}?`)) return;
    try {
      const token = await getToken();
      await window.fetch(`${API_BASE}/quality/standards/${it._id}`, { method:'DELETE', headers:{ Authorization:`Bearer ${token}` } });
      load();
    } catch {}
  };

  const fs = { background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text)', borderRadius:6, padding:'6px 10px', fontSize:13 };
  const inp = { ...fs, width:'100%', boxSizing:'border-box', padding:'8px 12px' };
  const labelS = { display:'block', fontSize:11, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'var(--ni-text-muted)', marginBottom:5 };
  const Badge = ({ text, color }) => <span style={{ fontSize:10, fontWeight:700, color, background:color+'20', borderRadius:4, padding:'1px 6px', whiteSpace:'nowrap' }}>{text}</span>;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8, flexWrap:'wrap', gap:10 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800 }}>Compliance Standards</h2>
        {canManage && (
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={seed} disabled={saving}
              style={{ background:'transparent', color:ACCENT, border:`1px solid ${ACCENT}66`, borderRadius:6, padding:'8px 14px', fontWeight:700, fontSize:13, cursor:'pointer' }}>
              Seed catalogue
            </button>
            <button onClick={()=>{ setEditItem({...BLANK}); setError(''); }}
              style={{ background:ACCENT, color:'#000', border:'none', borderRadius:6, padding:'8px 18px', fontWeight:700, fontSize:13, cursor:'pointer' }}>+ Add standard</button>
          </div>
        )}
      </div>
      <p style={{ margin:'0 0 18px', color:'var(--ni-text-muted)', fontSize:13 }}>
        The external standards North Invent builds and tests to. Used to populate the standards dropdowns across the QMS.
      </p>

      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <select value={category} onChange={e=>setCategory(e.target.value)} style={fs}>
          <option value="">All categories</option>
          {CATEGORIES.map(c=><option key={c}>{c}</option>)}
        </select>
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
          <input type="checkbox" checked={supplierOnly} onChange={e=>setSupplierOnly(e.target.checked)} />
          Supplier-applicable only
        </label>
      </div>

      <div style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', borderRadius:10, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--ni-text-muted)' }}>Loading…</div>
        : items.length===0 ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--ni-text-muted)' }}>
            No standards.{canManage && <> Click <button onClick={seed} style={{ background:'none', border:'none', color:ACCENT, cursor:'pointer', fontSize:14 }}>Seed catalogue</button> to load the curated NI list.</>}
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ color:'var(--ni-text-muted)', fontSize:11, textTransform:'uppercase', letterSpacing:'0.06em', background:'rgba(0,0,0,0.2)' }}>
                {['Code','Title','Authority','Category','Flags'].map(h=><th key={h} style={{ padding:'10px 16px', textAlign:'left', fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>)}
                {canManage && <th style={{ padding:'10px 16px' }}></th>}
              </tr>
            </thead>
            <tbody>
              {items.map(it=>(
                <tr key={it._id} style={{ borderTop:'1px solid var(--ni-border-soft)', opacity: it.active===false?0.5:1 }}>
                  <td style={{ padding:'11px 16px', fontFamily:'monospace', fontSize:12, color:ACCENT, whiteSpace:'nowrap' }}>{it.code}{it.edition?` · ${it.edition}`:''}</td>
                  <td style={{ padding:'11px 16px' }}>{it.title}</td>
                  <td style={{ padding:'11px 16px', color:'var(--ni-text-muted)', whiteSpace:'nowrap' }}>{it.authority}</td>
                  <td style={{ padding:'11px 16px', color:'var(--ni-text-muted)' }}>{it.category}</td>
                  <td style={{ padding:'11px 16px' }}>
                    <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                      {it.supplierApplicable && <Badge text="GPV" color="#2dd4bf" />}
                      {it.appliesToProduction && <Badge text="PRODUCTION" color="#f59e0b" />}
                      {it.active===false && <Badge text="INACTIVE" color="#94a3b8" />}
                    </div>
                  </td>
                  {canManage && (
                    <td style={{ padding:'6px 16px', whiteSpace:'nowrap' }}>
                      <button onClick={()=>{ setEditItem({ ...BLANK, ...it }); setError(''); }} style={{ background:'none', border:`1px solid ${ACCENT}44`, color:ACCENT, borderRadius:4, padding:'3px 10px', fontSize:11, cursor:'pointer', marginRight:6 }}>Edit</button>
                      <button onClick={()=>del(it)} style={{ background:'rgba(236,33,36,0.12)', border:'none', color:'#EC2124', borderRadius:4, padding:'3px 10px', fontSize:11, cursor:'pointer' }}>✕</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ marginTop:10, color:'var(--ni-text-muted)', fontSize:12 }}>{items.length} standard{items.length!==1?'s':''}</div>

      {editItem && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
          <div style={{ background:'var(--ni-char)', border:'1px solid var(--ni-border-soft)', borderRadius:12, padding:24, width:520, maxWidth:'100%', maxHeight:'90vh', overflowY:'auto' }}>
            <h3 style={{ margin:'0 0 16px', fontSize:16 }}>{editItem._id?'Edit':'Add'} standard</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <div><label style={labelS}>Code *</label><input value={editItem.code} onChange={e=>setEditItem(s=>({...s,code:e.target.value}))} placeholder="IEC 60945" style={inp} /></div>
              <div><label style={labelS}>Authority</label><input value={editItem.authority} onChange={e=>setEditItem(s=>({...s,authority:e.target.value}))} placeholder="IEC / IPC / ISO / EU" style={inp} /></div>
            </div>
            <div style={{ marginBottom:12 }}><label style={labelS}>Title</label><input value={editItem.title} onChange={e=>setEditItem(s=>({...s,title:e.target.value}))} style={inp} /></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <div><label style={labelS}>Category</label>
                <select value={editItem.category} onChange={e=>setEditItem(s=>({...s,category:e.target.value}))} style={inp}>{CATEGORIES.map(c=><option key={c}>{c}</option>)}</select></div>
              <div><label style={labelS}>Edition / Year</label><input value={editItem.edition} onChange={e=>setEditItem(s=>({...s,edition:e.target.value}))} placeholder="2024" style={inp} /></div>
            </div>
            <div style={{ marginBottom:12 }}><label style={labelS}>Scope</label><textarea value={editItem.scope} onChange={e=>setEditItem(s=>({...s,scope:e.target.value}))} rows={2} style={{ ...inp, resize:'vertical' }} /></div>
            <div style={{ marginBottom:12 }}><label style={labelS}>Reference URL</label><input value={editItem.url} onChange={e=>setEditItem(s=>({...s,url:e.target.value}))} placeholder="https://…" style={inp} /></div>
            <div style={{ marginBottom:12 }}><label style={labelS}>Notes</label><textarea value={editItem.notes} onChange={e=>setEditItem(s=>({...s,notes:e.target.value}))} rows={2} style={{ ...inp, resize:'vertical' }} /></div>
            <div style={{ display:'flex', gap:18, marginBottom:18, flexWrap:'wrap' }}>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}><input type="checkbox" checked={!!editItem.supplierApplicable} onChange={e=>setEditItem(s=>({...s,supplierApplicable:e.target.checked}))} />Supplier-applicable (GPV must comply)</label>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}><input type="checkbox" checked={!!editItem.appliesToProduction} onChange={e=>setEditItem(s=>({...s,appliesToProduction:e.target.checked}))} />Applies to production</label>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}><input type="checkbox" checked={editItem.active!==false} onChange={e=>setEditItem(s=>({...s,active:e.target.checked}))} />Active</label>
            </div>
            {error && <div style={{ color:'#EC2124', fontSize:13, marginBottom:12 }}>{error}</div>}
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={save} disabled={saving} style={{ background:ACCENT, color:'#000', border:'none', borderRadius:6, padding:'9px 20px', fontWeight:700, fontSize:13, cursor:'pointer' }}>{saving?'Saving…':'Save'}</button>
              <button onClick={()=>setEditItem(null)} style={{ background:'transparent', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text)', borderRadius:6, padding:'9px 16px', fontSize:13, cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
